import { MoreHorizontal, Network, Pencil, Plus, Search, Trash2, UsersRound } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  useDeleteTeam,
  useSaveTeam,
  useSetTeamMembers,
  useTeams,
  useUsers,
  type Team,
  type User,
} from "@/api/queries";
import { useMe } from "@/app/auth";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { Field } from "@/components/common/Field";
import { PageHeader } from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tooltip } from "@/components/ui/tooltip";
import { errorText, fieldError } from "@/lib/errors";

type Action = { kind: "create" } | { kind: "rename" | "members" | "delete"; team: Team };

export function TeamsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const admin = me.permissions.manage_teams;
  const teams = useTeams();
  const users = useUsers();
  const remove = useDeleteTeam();
  const [action, setAction] = useState<Action | null>(null);
  const close = () => setAction(null);
  const byId = useMemo(() => new Map((users.data ?? []).map((u) => [u.id, u])), [users.data]);

  return (
    <>
      <PageHeader
        title={t("teams.title")}
        description={admin ? t("teams.subtitle") : t("teams.subtitleOwn")}
        actions={
          admin && (
            <Button onClick={() => setAction({ kind: "create" })}>
              <Plus /> {t("teams.create")}
            </Button>
          )
        }
      />

      {teams.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44" />
          ))}
        </div>
      ) : teams.isError ? (
        <Card>
          <EmptyState icon={Network} title={t("common.error")} />
        </Card>
      ) : teams.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={Network}
            title={t("teams.emptyTitle")}
            description={admin ? t("teams.emptyDescription") : t("teams.emptyOwn")}
            action={
              admin && (
                <Button onClick={() => setAction({ kind: "create" })}>
                  <Plus /> {t("teams.create")}
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {teams.data.map((team) => (
            <TeamCard key={team.id} team={team} byId={byId} admin={admin} onAction={setAction} />
          ))}
        </div>
      )}

      {(action?.kind === "create" || action?.kind === "rename") && (
        <TeamDialog team={action.kind === "rename" ? action.team : undefined} onClose={close} />
      )}
      {action?.kind === "members" && (
        <MembersDialog team={action.team} users={users.data ?? []} onClose={close} />
      )}
      {action?.kind === "delete" && (
        <ConfirmDialog
          danger
          title={t("teams.deleteTitle", { name: action.team.name })}
          description={t("teams.deleteText")}
          confirmLabel={t("common.delete")}
          errorText={(err) => errorText(t, err)}
          onConfirm={() => remove.mutateAsync(action.team.id)}
          onClose={close}
        />
      )}
    </>
  );
}

type TeamCardProps = {
  team: Team;
  byId: Map<string, User>;
  admin: boolean;
  onAction: (a: Action) => void;
};

function TeamCard({ team, byId, admin, onAction }: TeamCardProps) {
  const { t } = useTranslation();
  const managers = team.members.filter((m) => m.is_manager);
  const members = team.members.filter((m) => !m.is_manager);
  const name = (id: string) => byId.get(id)?.full_name ?? "…";

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold">{team.name}</h2>
          {team.description && <p className="mt-0.5 line-clamp-2 text-muted">{team.description}</p>}
        </div>
        {admin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={`${t("common.actions")}: ${team.name}`}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onSelect={() => onAction({ kind: "members", team })}>
                <UsersRound /> {t("teams.editMembers")}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onAction({ kind: "rename", team })}>
                <Pencil /> {t("teams.rename")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-danger" onSelect={() => onAction({ kind: "delete", team })}>
                <Trash2 /> {t("teams.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted uppercase">{t("teams.managers")}</span>
        {managers.length === 0 ? (
          <Badge variant="warning" className="self-start">
            {t("teams.noManager")}
          </Badge>
        ) : (
          <div className="flex flex-wrap gap-2">
            {managers.map((m) => (
              <span key={m.user_id} className="inline-flex items-center gap-1.5">
                <UserAvatar name={name(m.user_id)} className="size-6 text-[10px]" />
                <span className="font-medium">{name(m.user_id)}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {members.slice(0, 6).map((m) => (
              <Tooltip key={m.user_id} content={name(m.user_id)}>
                <span>
                  <UserAvatar name={name(m.user_id)} className="size-7 text-[10px] ring-2 ring-surface" />
                </span>
              </Tooltip>
            ))}
          </div>
          <span className="text-xs text-muted">
            {t("teams.membersCount", { count: team.members.length })}
          </span>
        </div>
        {admin && (
          <Button variant="ghost" size="sm" onClick={() => onAction({ kind: "members", team })}>
            <UsersRound /> {t("teams.editMembers")}
          </Button>
        )}
      </div>
    </Card>
  );
}

function TeamDialog({ team, onClose }: { team?: Team; onClose: () => void }) {
  const { t } = useTranslation();
  const save = useSaveTeam();
  const [name, setName] = useState(team?.name ?? "");
  const [description, setDescription] = useState(team?.description ?? "");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync({ teamId: team?.id, name: name.trim(), description: description.trim() });
      onClose();
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={team ? t("teams.editTitle", { name: team.name }) : t("teams.create")}>
        <form className="flex flex-col gap-4 p-5" onSubmit={(e) => void submit(e)}>
          <Field label={t("teams.name")} error={fieldError(t, save.error, "name")}>
            <Input
              required
              autoFocus
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label={`${t("teams.description")} (${t("common.optional")})`}>
            <textarea
              rows={3}
              maxLength={2000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-lg border bg-surface px-3 py-2 shadow-card"
            />
          </Field>
          {save.error && !fieldError(t, save.error, "name") && (
            <Alert variant="danger">{errorText(t, save.error)}</Alert>
          )}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              {team ? t("common.save") : t("common.create")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({ team, users, onClose }: { team: Team; users: User[]; onClose: () => void }) {
  const { t } = useTranslation();
  const setMembers = useSetTeamMembers();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Map<string, boolean>>(
    () => new Map(team.members.map((m) => [m.user_id, m.is_manager])),
  );
  const candidates = users.filter((u) => u.status !== "blocked" || selected.has(u.id));
  const q = query.trim().toLowerCase();
  const visible = candidates.filter(
    (u) => !q || u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
  );

  const update = (id: string, value: boolean | null) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (value === null) next.delete(id);
      else next.set(id, value);
      return next;
    });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await setMembers.mutateAsync({
        teamId: team.id,
        members: [...selected].map(([user_id, is_manager]) => ({ user_id, is_manager })),
      });
      onClose();
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={t("teams.membersTitle", { name: team.name })}
        description={t("teams.membersHint")}
        className="w-[min(92vw,600px)]"
      >
        <form className="flex flex-col gap-4 p-5" onSubmit={(e) => void submit(e)}>
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <Input
              type="search"
              aria-label={t("teams.filter")}
              placeholder={t("teams.filter")}
              className="pl-9"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <ul className="flex max-h-[50dvh] flex-col gap-0.5 overflow-y-auto">
            {visible.length === 0 && <li className="px-2 py-3 text-muted">{t("common.noResults")}</li>}
            {visible.map((u) => {
              const member = selected.has(u.id);
              const canManage = u.role !== "employee";
              return (
                <li
                  key={u.id}
                  className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-surface-muted"
                >
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={member}
                      onChange={() => update(u.id, member ? null : false)}
                      className="size-4 accent-(--primary-solid)"
                    />
                    <UserAvatar name={u.full_name} className="size-7" />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{u.full_name}</span>
                      <span className="block truncate text-xs text-muted">{t(`roles.${u.role}`)}</span>
                    </span>
                  </label>
                  {member && (
                    <label
                      className="flex items-center gap-2 text-sm"
                      title={canManage ? undefined : t("teams.managerOnlyStaff")}
                    >
                      <span className={canManage ? "" : "text-muted"}>{t("teams.manager")}</span>
                      <Switch
                        checked={selected.get(u.id) ?? false}
                        disabled={!canManage}
                        onCheckedChange={(v) => update(u.id, v)}
                        aria-label={`${t("teams.manager")}: ${u.full_name}`}
                      />
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
          {setMembers.error && <Alert variant="danger">{errorText(t, setMembers.error)}</Alert>}
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted">{t("teams.membersCount", { count: selected.size })}</span>
            <div className="flex gap-2">
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  {t("common.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={setMembers.isPending}>
                {t("common.save")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
